import { useEffect, useRef, useState } from "react";
import {
    SearchIcon,
    ChevronUpIcon,
    ChevronDownIcon,
    ChevronsUpDownIcon,
    UsersIcon,
    StarIcon,
    CheckCircle2Icon,
    UserIcon,
} from "lucide-react";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@component/select";

const TL_NONE = "__none__";
import TrainingAuthLayout from "@container/layout/training-auth";
import { Card, CardContent } from "@component/card";
import { Pagination } from "@component/pagination";
import { StatTile } from "@component/training/stat-tile";
import { EmployeeProfileDrawer } from "@component/training/employee-profile-drawer";
import { formatSessionStatus } from "@type/training";
import {
    TrainingErrorCard,
    TrainingSkeletonRows,
} from "@component/training/training-async-state";
import { catchErrorLog } from "@util/sentry";
import {
    getEmployeeActivityLog,
    getEmployeeAttempts,
    getReportsEmployees,
    getReportsEmployeesSummary,
    getTeamLeaders,
    listCampaigns,
    pickPersonaName,
} from "@service/training";
import type {
    ActivityEvent,
    ReportsEmployeeRow,
    ReportsEmployeesResult,
    ReportsEmployeeSortBy,
    ReportsEmployeeSortDir,
    ReportsEmployeeStatusFilter,
    ReportsEmployeesSummary,
} from "@service/training";
import type { TrainingSession } from "@type/training";
import { cn } from "@util/tailwind";

interface TrainingPeoplePageProps {
    embedded?: boolean;
}

const PAGE_SIZE = 10;

function formatDate(dateStr: string | null | undefined): string {
    if (!dateStr) return "—";
    return new Date(dateStr).toLocaleDateString("en-MY", {
        day: "numeric",
        month: "short",
        year: "numeric",
    });
}

// Colour only — the LABEL is the BE status shown verbatim via
// formatSessionStatus (no remapping, per BE: "started" reads "Started", not
// "In Progress"). The BE owns the status vocabulary.
function statusLabel(status: string): { label: string; cls: string } {
    const cls: Record<string, string> = {
        completed: "bg-emerald-50 text-emerald-700",
        in_progress: "bg-amber-50 text-amber-700",
        started: "bg-amber-50 text-amber-700",
        scoring: "bg-blue-50 text-blue-700",
        invited: "bg-gray-100 text-gray-600",
        failed: "bg-rose-50 text-rose-700",
        expired: "bg-amber-50 text-amber-700",
        abandoned: "bg-gray-100 text-gray-500",
        pending: "bg-gray-100 text-gray-600",
    };
    return {
        label: formatSessionStatus(status),
        cls: cls[status] ?? "bg-gray-100 text-gray-500",
    };
}

export default function TrainingPeoplePage({ embedded = false }: TrainingPeoplePageProps = {}) {
    const [summary, setSummary] = useState<ReportsEmployeesSummary | null>(null);
    const [summaryLoading, setSummaryLoading] = useState(true);

    const [result, setResult] = useState<ReportsEmployeesResult | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<Error | null>(null);
    const [reloadTick, setReloadTick] = useState(0);

    const [search, setSearch] = useState("");
    const [debouncedSearch, setDebouncedSearch] = useState("");
    const [statusFilter, setStatusFilter] = useState<ReportsEmployeeStatusFilter>("all");
    const [teamLeader, setTeamLeader] = useState("");
    const [teamLeaders, setTeamLeaders] = useState<string[]>([]);
    const [sortBy, setSortBy] = useState<ReportsEmployeeSortBy>("last_attempt");
    const [sortDir, setSortDir] = useState<ReportsEmployeeSortDir>("desc");
    const [page, setPage] = useState(1);

    // Drawer detail. The list endpoint returns only summary fields per row, so
    // the full attempt history (needed by the drawer chart) is fetched per
    // employee on row click via the attempts-by-email endpoint.
    const [selectedRow, setSelectedRow] = useState<ReportsEmployeeRow | null>(null);
    const [selectedAttempts, setSelectedAttempts] = useState<TrainingSession[]>([]);
    const [selectedTimeline, setSelectedTimeline] = useState<ActivityEvent[]>([]);
    const [attemptsLoading, setAttemptsLoading] = useState(false);
    // Guards against out-of-order responses when rows are clicked quickly.
    const attemptsReqRef = useRef(0);
    // Deep-link support: ?openEmployee=<email> (e.g. from the dashboard's Team
    // insights rows) seeds the search with that email so the row surfaces, then
    // auto-opens its profile drawer once. The ref makes it a one-shot.
    const autoOpenDone = useRef(false);
    useEffect(() => {
        const raw = new URLSearchParams(window.location.search).get("openEmployee");
        if (!raw) return;
        setSearch(decodeURIComponent(raw));
    }, []);

    // Top KPI cards (aggregate across all campaigns).
    useEffect(() => {
        let cancelled = false;
        setSummaryLoading(true);
        getReportsEmployeesSummary()
            .then((s) => { if (!cancelled) setSummary(s); })
            .catch(() => {})
            .finally(() => { if (!cancelled) setSummaryLoading(false); });
        return () => { cancelled = true; };
    }, [reloadTick]);

    // Distinct assigned team leaders for the filter dropdown.
    useEffect(() => {
        getTeamLeaders().then(setTeamLeaders).catch(() => {});
    }, [reloadTick]);

    // Debounce the search box and reset to page 1 whenever the query settles.
    useEffect(() => {
        const t = setTimeout(() => {
            setDebouncedSearch(search);
            setPage(1);
        }, 300);
        return () => clearTimeout(t);
    }, [search]);

    // Server-side employees list: page / status / search.
    useEffect(() => {
        let cancelled = false;
        setLoading(true);
        setError(null);
        getReportsEmployees({
            page,
            limit: PAGE_SIZE,
            status: statusFilter,
            search: debouncedSearch,
            // Only send team_leader when the API actually returns TL data.
            // teamLeaders is populated by getTeamLeaders(); an empty array means
            // the backend hasn't shipped employee_team_leader yet, so filtering
            // by it would silently return wrong results.
            team_leader: teamLeaders.length > 0 ? (teamLeader || undefined) : undefined,
            sort_by: sortBy,
            sort: sortDir,
        })
            .then((res) => {
                if (cancelled) return;
                setResult(res);
                setLoading(false);
            })
            .catch((err: unknown) => {
                if (cancelled) return;
                const e = err instanceof Error ? err : new Error(String(err));
                catchErrorLog(`[training] employees load failed: ${e.message}`);
                setError(e);
                setLoading(false);
            });
        return () => { cancelled = true; };
    }, [page, statusFilter, debouncedSearch, teamLeader, sortBy, sortDir, reloadTick]);

    const handleStatus = (next: ReportsEmployeeStatusFilter) => {
        setStatusFilter(next);
        setPage(1);
    };

    const handleTeamLeader = (next: string) => {
        setTeamLeader(next);
        setPage(1);
    };

    const toggleSort = (field: ReportsEmployeeSortBy) => {
        if (sortBy === field) {
            setSortDir((d) => (d === "asc" ? "desc" : "asc"));
        } else {
            setSortBy(field);
            setSortDir("asc");
        }
        setPage(1);
    };

    const handleRowClick = async (row: ReportsEmployeeRow) => {
        const reqId = ++attemptsReqRef.current;
        setSelectedRow(row); // open the drawer immediately (shows a skeleton)
        setSelectedAttempts([]);
        setSelectedTimeline([]);
        setAttemptsLoading(true);
        try {
            const [campaigns, timeline] = await Promise.all([
                listCampaigns(),
                getEmployeeActivityLog(row.employee_email),
            ]);
            if (attemptsReqRef.current !== reqId) return;
            // Fetch attempts per campaign in parallel so each attempt carries the
            // correct campaign_id and campaign_name — the flat attempts endpoint
            // doesn't return those fields, so we derive them from the campaign list.
            const perCampaign = await Promise.allSettled(
                campaigns.map(async (c) => {
                    const attempts = await getEmployeeAttempts(row.employee_email, c.id);
                    if (attempts.length === 0) return null;
                    return attempts.map((a) => ({
                        ...a,
                        campaign_id: c.id,
                        campaign_name: c.name,
                        persona_name: c.persona_name ?? pickPersonaName(c.persona_id),
                        team_leader: c.team_leader ?? null,
                    }));
                }),
            );
            if (attemptsReqRef.current === reqId) {
                const allAttempts = (perCampaign as PromiseSettledResult<TrainingSession[] | null>[])
                    .filter((r): r is PromiseFulfilledResult<TrainingSession[]> =>
                        r.status === "fulfilled" && r.value !== null)
                    .flatMap((r) => r.value);
                setSelectedAttempts(allAttempts);
                setSelectedTimeline(timeline);
            }
        } catch (err) {
            catchErrorLog(
                `[training] employee detail load failed: ${
                    err instanceof Error ? err.message : String(err)
                }`,
            );
            if (attemptsReqRef.current === reqId) {
                setSelectedAttempts([]);
                setSelectedTimeline([]);
            }
        } finally {
            if (attemptsReqRef.current === reqId) setAttemptsLoading(false);
        }
    };

    const rows = result?.rows ?? [];
    const counts = result?.status_counts ?? { all: 0, completed: 0, in_progress: 0, pending: 0 };
    const pagination = result?.pagination ?? { page, limit: PAGE_SIZE, total: 0, total_pages: 0 };

    // Once the (search-seeded) results arrive, auto-open the deep-linked
    // employee's drawer a single time.
    useEffect(() => {
        if (autoOpenDone.current) return;
        const raw = new URLSearchParams(window.location.search).get("openEmployee");
        if (!raw) return;
        const decoded = decodeURIComponent(raw);
        const row = (result?.rows ?? []).find((r) => r.employee_email === decoded);
        if (row) {
            autoOpenDone.current = true;
            handleRowClick(row);
        }
    }, [result]);

    const body = (
        <>
            <div>
                {!embedded && (
                    <div className="mb-6">
                        <h1 className="text-2xl font-semibold text-gray-900">Employees</h1>
                        <p className="text-sm text-gray-600 mt-1">
                            Every employee enrolled across your training campaigns.
                        </p>
                    </div>
                )}

                {/* Stat tiles — sourced from the employees-summary endpoint. */}
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 mb-6">
                    <StatTile
                        label="People enrolled"
                        value={summary?.people_enrolled ?? "—"}
                        loading={summaryLoading}
                        icon={UsersIcon}
                        tooltip="Total number of employees enrolled across all campaigns."
                    />
                    <StatTile
                        label="Avg score"
                        value={summary?.avg_score != null ? `${Math.round(summary.avg_score)}/100` : "—"}
                        loading={summaryLoading}
                        icon={StarIcon}
                        tooltip="Average score across all completed training sessions, out of 100."
                    />
                    <StatTile
                        label="Completion rate"
                        value={summary ? `${Math.round(summary.completion_rate)}%` : "—"}
                        badge={summary ? `${summary.total_completed}/${summary.total_employees}` : undefined}
                        loading={summaryLoading}
                        icon={CheckCircle2Icon}
                        tooltip="Percentage of enrolled employees who have completed their training."
                    />
                </div>

                {/* Search + filters bar */}
                <div className="flex flex-col sm:flex-row sm:items-center gap-3 mb-4">
                    <div className="relative flex-1 min-w-0">
                        <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                        <input
                            type="text"
                            placeholder="Search name, email, role, department..."
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                    </div>
                    {/* Status dropdown filter with counts */}
                    <Select value={statusFilter} onValueChange={(v) => handleStatus(v as ReportsEmployeeStatusFilter)}>
                        <SelectTrigger className="w-[180px] text-sm flex-shrink-0">
                            <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                            {([
                                { key: "all", label: "All Statuses" },
                                { key: "completed", label: "Completed" },
                                { key: "in_progress", label: "In Progress" },
                                { key: "pending", label: "Invited" },
                            ] as { key: ReportsEmployeeStatusFilter; label: string }[]).map((opt) => (
                                <SelectItem key={opt.key} value={opt.key}>
                                    {opt.label}{" "}
                                    <span className="text-gray-400">({counts[opt.key]})</span>
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                    {(() => {
                        const options = teamLeaders.length > 0
                            ? teamLeaders
                            : Array.from(new Set(rows.map((r) => r.employee_team_leader).filter((tl): tl is string => !!tl))).sort();
                        return options.length > 0 ? (
                            <Select value={teamLeader || TL_NONE} onValueChange={(v) => handleTeamLeader(v === TL_NONE ? "" : v)}>
                                <SelectTrigger className="w-[180px] text-sm flex-shrink-0">
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value={TL_NONE}>All team leaders</SelectItem>
                                    {options.map((tl) => (
                                        <SelectItem key={tl} value={tl}>{tl}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        ) : null;
                    })()}
                </div>

                {error ? (
                    <TrainingErrorCard
                        title="We couldn't load your employees."
                        onRetry={() => setReloadTick((t) => t + 1)}
                    />
                ) : loading ? (
                    <Card>
                        <CardContent className="p-5">
                            <TrainingSkeletonRows rows={6} />
                        </CardContent>
                    </Card>
                ) : pagination.total === 0 && !debouncedSearch && statusFilter === "all" ? (
                    <Card>
                        <div className="flex flex-col items-center justify-center py-16 px-4 text-center">
                            <div className="bg-gray-100 p-4 rounded-full mb-4">
                                <UsersIcon className="h-8 w-8 text-gray-400" />
                            </div>
                            <h3 className="text-sm font-medium text-gray-900 mb-2">
                                No employees yet.
                            </h3>
                            <p className="text-xs text-gray-500 max-w-xs">
                                Create a campaign and invite employees to get started.
                            </p>
                        </div>
                    </Card>
                ) : (
                    <>
                        <div className="rounded-lg border border-gray-200 overflow-hidden">
                            <table className="w-full text-sm">
                                <thead>
                                    <tr className="bg-gray-50 border-b border-gray-200">
                                        <Th label="Employee" field="name" activeField={sortBy} dir={sortDir} onSort={toggleSort} align="left" />
                                        <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wide">Role • Dept</th>
                                        <Th label="Total Attempts" field="attempts" activeField={sortBy} dir={sortDir} onSort={toggleSort} align="center" />
                                        <Th label="Last Attempt" field="last_attempt" activeField={sortBy} dir={sortDir} onSort={toggleSort} align="center" />
                                        <Th label="Latest Score" field="latest_score" activeField={sortBy} dir={sortDir} onSort={toggleSort} align="center" />
                                        <Th label="Avg Score" field="avg_score" activeField={sortBy} dir={sortDir} onSort={toggleSort} align="center" />
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-100">
                                    {rows.length === 0 ? (
                                        <tr>
                                            <td colSpan={6} className="px-4 py-10 text-center text-sm text-gray-500 italic">
                                                No employees match your search.
                                            </td>
                                        </tr>
                                    ) : (
                                        rows.map((row) => (
                                            <EmployeeRow
                                                key={row.employee_email}
                                                row={row}
                                                onClick={() => handleRowClick(row)}
                                            />
                                        ))
                                    )}
                                </tbody>
                            </table>
                        </div>

                        {/* Pagination */}
                        <Pagination
                            currentPage={pagination.page}
                            totalPages={pagination.total_pages}
                            total={pagination.total}
                            pageSize={pagination.limit}
                            itemLabel="employee"
                            onPageChange={setPage}
                            className="mt-4"
                        />
                    </>
                )}
            </div>

            {selectedRow && (
                <EmployeeProfileDrawer
                    open={!!selectedRow}
                    onClose={() => {
                        setSelectedRow(null);
                        setSelectedAttempts([]);
                        setSelectedTimeline([]);
                    }}
                    name={selectedRow.employee_name}
                    email={selectedRow.employee_email}
                    role={selectedRow.employee_role}
                    department={selectedRow.employee_department}
                    personaName="Roleplay"
                    attempts={selectedAttempts}
                    timeline={selectedTimeline}
                    loading={attemptsLoading}
                />
            )}
        </>
    );

    return embedded ? body : <TrainingAuthLayout>{body}</TrainingAuthLayout>;
}

function EmployeeRow({ row, onClick }: { row: ReportsEmployeeRow; onClick: () => void }) {
    const { label, cls } = statusLabel(row.status);
    const roleDept = [row.employee_role, row.employee_department].filter(Boolean).join(" • ");

    return (
        <tr onClick={onClick} className="cursor-pointer hover:bg-gray-50 transition-colors">
            {/* Employee name + email */}
            <td className="px-4 py-3">
                <div className="flex items-center gap-3 min-w-0">
                    <div className="flex-shrink-0 w-9 h-9 flex items-center justify-center rounded-lg bg-blue-50">
                        <UserIcon className="w-4 h-4 text-blue-600" />
                    </div>
                    <div className="min-w-0">
                        <div className="font-medium text-gray-900 truncate">{row.employee_name}</div>
                        <div className="text-xs text-gray-500 truncate">{row.employee_email}</div>
                    </div>
                </div>
            </td>
            {/* Role / Dept */}
            <td className="px-4 py-3 text-center">
                <div className="text-sm text-gray-700 truncate">
                    {roleDept || <span className="text-gray-400">—</span>}
                </div>
            </td>
            {/* Attempts */}
            <td className="px-4 py-3 tabular-nums text-gray-700 text-center">{row.attempts}</td>
            {/* Last attempt */}
            <td className="px-4 py-3 text-gray-600 whitespace-nowrap text-center">{formatDate(row.last_attempt)}</td>
            {/* Latest score */}
            <td className="px-4 py-3 tabular-nums text-center">
                {row.latest_score !== null ? (
                    <span className="font-semibold text-gray-900">{row.latest_score}<span className="text-gray-400 font-normal">/100</span></span>
                ) : <span className="text-gray-400">—</span>}
            </td>
            {/* Avg score */}
            <td className="px-4 py-3 tabular-nums text-gray-700 text-center">
                {row.avg_score !== null ? (
                    <>{Math.round(row.avg_score)}<span className="text-gray-400">/100</span></>
                ) : <span className="text-gray-400">—</span>}
            </td>
        </tr>
    );
}

function Th({
    label,
    field,
    activeField,
    dir,
    onSort,
    align = "left",
}: {
    label: string;
    field: ReportsEmployeeSortBy;
    activeField: ReportsEmployeeSortBy;
    dir: ReportsEmployeeSortDir;
    onSort: (f: ReportsEmployeeSortBy) => void;
    align?: "left" | "center";
}) {
    const isActive = activeField === field;
    return (
        <th
            className={cn(
                "px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wide cursor-pointer select-none hover:text-gray-700",
                align === "center" ? "text-center" : "text-left",
            )}
            onClick={() => onSort(field)}
        >
            <div className={cn("flex items-center gap-1", align === "center" && "justify-center")}>
                {label}
                {isActive ? (
                    dir === "asc"
                        ? <ChevronUpIcon className="w-3.5 h-3.5 text-blue-600" />
                        : <ChevronDownIcon className="w-3.5 h-3.5 text-blue-600" />
                ) : (
                    <ChevronsUpDownIcon className="w-3.5 h-3.5 text-gray-300" />
                )}
            </div>
        </th>
    );
}

